import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  Truck, MapPin, Shield, Phone, Mail, Globe, Star, DollarSign,
  Loader2, ChevronRight, Send, Calendar, ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import ReviewSection from "@/components/ReviewSection";
import { logger } from "@/utils/logger";

const SPECIES_OPTIONS = ["canine", "feline", "equine", "cattle", "exotics"];

const TransporterDetail = ({ user, login }) => {
  const { transporterId } = useParams();
  const [transporter, setTransporter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    pickup_city: "", pickup_state: "", delivery_city: "", delivery_state: "",
    species: "", animal_count: 1, preferred_date: "", notes: "", request_type: "quote"
  });

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/transporters/${transporterId}`);
        setTransporter(res.data.transporter);
      } catch (err) { logger.error("Fetch error:", err); } finally { setLoading(false); }
    };
    fetch();
  }, [transporterId]);

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!user) { login(`/transporters/${transporterId}`); return; }
    if (!bookingForm.pickup_state || !bookingForm.delivery_state) {
      toast.error("Pickup and delivery states are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/transporters/${transporterId}/book`, bookingForm);
      toast.success("Request sent! The transporter will respond shortly.");
      setShowBooking(false);
      setBookingForm({ pickup_city: "", pickup_state: "", delivery_city: "", delivery_state: "", species: "", animal_count: 1, preferred_date: "", notes: "", request_type: "quote" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send request");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (f, v) => setBookingForm(prev => ({ ...prev, [f]: v }));

  if (loading) return <div className="pt-24 min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" /></div>;
  if (!transporter) return <div className="pt-24 min-h-screen bg-[#050505] flex items-center justify-center"><p className="text-[#A1A1AA]">Transporter not found</p></div>;

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-[#71717A] mb-6">
          <Link to="/transporters" className="hover:text-[#D4AF37]">Transporters</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-[#A1A1AA]">{transporter.business_name}</span>
        </div>

        {/* Header */}
        <div className="glass-card p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Truck className="w-6 h-6 text-[#D4AF37]" />
                <h1 className="text-3xl text-[#EDEDED]" data-testid="transporter-name">{transporter.business_name}</h1>
                {transporter.is_verified && (
                  <span className="flex items-center gap-1 text-sm text-[#22C55E] bg-[#22C55E]/10 px-2 py-0.5 rounded-full">
                    <Shield className="w-3.5 h-3.5" /> Verified
                  </span>
                )}
              </div>
              {transporter.description && <p className="text-[#A1A1AA] mb-4">{transporter.description}</p>}
              
              <div className="flex flex-wrap gap-4 text-sm text-[#A1A1AA]">
                {transporter.service_area && (
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-[#D4AF37]" />{transporter.service_area}</span>
                )}
                {transporter.base_rate_per_mile && (
                  <span className="flex items-center gap-1"><DollarSign className="w-4 h-4 text-[#D4AF37]" />${transporter.base_rate_per_mile}/mile</span>
                )}
                {transporter.species_handled && transporter.species_handled.length > 0 && (
                  <span className="capitalize">{transporter.species_handled.join(", ")}</span>
                )}
              </div>
            </div>
            <button
              onClick={() => { if (!user) login(`/transporters/${transporterId}`); else setShowBooking(!showBooking); }}
              className="btn-gold text-sm px-5 py-2.5 flex-shrink-0"
              data-testid="request-booking-btn"
            >
              <Send className="w-4 h-4 inline mr-1" /> Request {transporter.base_rate_per_mile ? "Booking" : "Quote"}
            </button>
          </div>

          {/* Contact */}
          <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap gap-4 text-sm">
            {transporter.phone && (
              <a href={`tel:${transporter.phone}`} className="flex items-center gap-1 text-[#A1A1AA] hover:text-[#D4AF37]">
                <Phone className="w-4 h-4" /> {transporter.phone}
              </a>
            )}
            {transporter.email && (
              <a href={`mailto:${transporter.email}`} className="flex items-center gap-1 text-[#A1A1AA] hover:text-[#D4AF37]">
                <Mail className="w-4 h-4" /> {transporter.email}
              </a>
            )}
            {transporter.website && (
              <a href={transporter.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#A1A1AA] hover:text-[#D4AF37]">
                <Globe className="w-4 h-4" /> Website
              </a>
            )}
          </div>
        </div>

        {/* Booking Form */}
        {showBooking && (
          <form onSubmit={handleBooking} className="glass-card p-6 mb-6 space-y-4" data-testid="booking-form">
            <h3 className="text-lg text-[#EDEDED]">Request {transporter.base_rate_per_mile ? "Booking" : "Quote"}</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#71717A] mb-1">Pickup City</label>
                <input value={bookingForm.pickup_city} onChange={(e) => update("pickup_city", e.target.value)} className="input-minimal w-full" placeholder="City" />
              </div>
              <div>
                <label className="block text-xs text-[#71717A] mb-1">Pickup State *</label>
                <input value={bookingForm.pickup_state} onChange={(e) => update("pickup_state", e.target.value)} className="input-minimal w-full" placeholder="e.g. GA" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#71717A] mb-1">Delivery City</label>
                <input value={bookingForm.delivery_city} onChange={(e) => update("delivery_city", e.target.value)} className="input-minimal w-full" placeholder="City" />
              </div>
              <div>
                <label className="block text-xs text-[#71717A] mb-1">Delivery State *</label>
                <input value={bookingForm.delivery_state} onChange={(e) => update("delivery_state", e.target.value)} className="input-minimal w-full" placeholder="e.g. TX" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-[#71717A] mb-1">Species</label>
                <select value={bookingForm.species} onChange={(e) => update("species", e.target.value)} className="input-minimal w-full">
                  <option value="">Select...</option>
                  {SPECIES_OPTIONS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#71717A] mb-1">Animals</label>
                <input type="number" min="1" value={bookingForm.animal_count} onChange={(e) => update("animal_count", parseInt(e.target.value) || 1)} className="input-minimal w-full" />
              </div>
              <div>
                <label className="block text-xs text-[#71717A] mb-1">Preferred Date</label>
                <input type="date" value={bookingForm.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} className="input-minimal w-full" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-[#71717A] mb-1">Notes</label>
              <textarea value={bookingForm.notes} onChange={(e) => update("notes", e.target.value)} className="input-minimal w-full min-h-[60px]" placeholder="Any special requirements..." />
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={submitting} className="btn-gold text-sm px-5 py-2 disabled:opacity-50" data-testid="submit-booking-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Request"}
              </button>
              <button type="button" onClick={() => setShowBooking(false)} className="btn-secondary text-sm px-4 py-2">Cancel</button>
            </div>
          </form>
        )}

        {/* Reviews */}
        <div className="glass-card p-6">
          <h3 className="text-lg text-[#EDEDED] mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-[#D4AF37]" /> Reviews
          </h3>
          <ReviewSection targetType="transporter" targetId={transporterId} user={user} />
        </div>
      </div>
    </div>
  );
};

export default TransporterDetail;
